import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, MessageCircle, TrendingUp, Search, X,
  UserPlus, ChevronRight, Sparkles, Heart, Activity,
  Brain, Wind, Zap, Shield, Eye, Ear, Dna, Droplets,
  Baby, Bone, Leaf, Star
} from 'lucide-react';
import { communityService, Disease } from '../../services/communityService';
import PageHeader from '../../components/PageHeader';

// Romanian translations for categories
const CATEGORY_TRANSLATIONS: Record<string, string> = {
  CARDIOVASCULAR: 'Cardiovasculare',
  METABOLIC: 'Metabolice',
  RESPIRATORY: 'Respiratorii',
  AUTOIMMUNE: 'Autoimune',
  GASTROINTESTINAL: 'Digestive',
  NEUROLOGICAL: 'Neurologice',
  ENDOCRINE: 'Endocrine',
  MENTAL_HEALTH: 'Sănătate mintală',
  KIDNEY: 'Renale',
  REPRODUCTIVE: 'Reproductive',
  MUSCULOSKELETAL: 'Musculo-scheletice',
  DERMATOLOGICAL: 'Dermatologice',
  HEMATOLOGIC: 'Hematologice',
  OCULAR: 'Oculare',
  ORL: 'ORL',
  RARE_GENETIC: 'Rare / genetice'
};

// Category icons & colors
const CATEGORY_META: Record<string, { icon: React.ElementType; gradient: string; bg: string; text: string; border: string }> = {
  CARDIOVASCULAR:   { icon: Heart,        gradient: 'from-red-500 to-rose-600',      bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200' },
  METABOLIC:        { icon: Zap,          gradient: 'from-amber-500 to-orange-600',  bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200' },
  RESPIRATORY:      { icon: Wind,         gradient: 'from-sky-500 to-blue-600',      bg: 'bg-sky-50',    text: 'text-sky-700',    border: 'border-sky-200' },
  AUTOIMMUNE:       { icon: Shield,       gradient: 'from-purple-500 to-violet-600', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  GASTROINTESTINAL: { icon: Activity,     gradient: 'from-green-500 to-emerald-600', bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200' },
  NEUROLOGICAL:     { icon: Brain,        gradient: 'from-indigo-500 to-blue-700',   bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  ENDOCRINE:        { icon: Leaf,         gradient: 'from-teal-500 to-cyan-600',     bg: 'bg-teal-50',   text: 'text-teal-700',   border: 'border-teal-200' },
  MENTAL_HEALTH:    { icon: Sparkles,     gradient: 'from-pink-500 to-rose-500',     bg: 'bg-pink-50',   text: 'text-pink-700',   border: 'border-pink-200' },
  KIDNEY:           { icon: Droplets,     gradient: 'from-blue-500 to-cyan-600',     bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200' },
  REPRODUCTIVE:     { icon: Baby,         gradient: 'from-fuchsia-500 to-pink-600',  bg: 'bg-fuchsia-50',text: 'text-fuchsia-700',border: 'border-fuchsia-200' },
  MUSCULOSKELETAL:  { icon: Bone,         gradient: 'from-stone-500 to-gray-600',    bg: 'bg-stone-50',  text: 'text-stone-700',  border: 'border-stone-200' },
  DERMATOLOGICAL:   { icon: Star,         gradient: 'from-yellow-500 to-amber-600',  bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  HEMATOLOGIC:      { icon: Droplets,     gradient: 'from-red-600 to-rose-700',      bg: 'bg-red-50',    text: 'text-red-800',    border: 'border-red-300' },
  OCULAR:           { icon: Eye,          gradient: 'from-cyan-500 to-teal-600',     bg: 'bg-cyan-50',   text: 'text-cyan-700',   border: 'border-cyan-200' },
  ORL:              { icon: Ear,          gradient: 'from-orange-500 to-amber-600',  bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  RARE_GENETIC:     { icon: Dna,          gradient: 'from-violet-500 to-purple-700', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
};

const DEFAULT_META = { icon: Activity, gradient: 'from-gray-500 to-gray-600', bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };

export default function CommunityHomePage() {
  const navigate = useNavigate();
  const [diseases, setDiseases] = useState<Disease[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadDiseases();
  }, []);

  const loadDiseases = async () => {
    try {
      setLoading(true);
      const data = await communityService.getDiseases();
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name, 'ro'));
      setDiseases(sortedData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la încărcarea comunităților');
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(diseases.map(d => d.category))].sort((a, b) => {
    const nameA = CATEGORY_TRANSLATIONS[a] || a;
    const nameB = CATEGORY_TRANSLATIONS[b] || b;
    return nameA.localeCompare(nameB, 'ro');
  });

  const filteredDiseases = diseases
    .filter(d => filter === 'all' || d.category === filter)
    .filter(d => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        d.name.toLowerCase().includes(q) ||
        (d.shortDescription || '').toLowerCase().includes(q) ||
        (CATEGORY_TRANSLATIONS[d.category] || d.category).toLowerCase().includes(q)
      );
    });

  const topCommunities = [...diseases]
    .sort((a, b) => b.memberCount - a.memberCount)
    .slice(0, 6);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
            <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-500 font-medium">Se încarcă comunitățile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <PageHeader
        title="Comunitate"
        showBack={true}
        backTo="/dashboard"
      />

      {/* ─── HERO BANNER ─── */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 px-4 pt-6 pb-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="absolute top-1/2 right-16 w-24 h-24 bg-white/5 rounded-full" />

        <div className="relative max-w-5xl mx-auto">
          {/* Desktop: two-column hero */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <span className="text-blue-200 text-sm font-medium">{diseases.length} comunități active</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
                Antrenează-te alături de alții 💪
              </h1>
              <p className="text-blue-200 text-sm md:text-base leading-relaxed max-w-md">
                Conectează-te cu oameni care au aceleași obiective ca tine
              </p>
            </div>

            {/* Desktop quick actions in hero */}
            <div className="hidden md:flex flex-col gap-2 flex-shrink-0">
              <button
                onClick={() => navigate('/community/friends')}
                className="flex items-center gap-3 px-5 py-3 bg-white/15 hover:bg-white/25 rounded-xl text-white transition-all border border-white/20"
              >
                <UserPlus className="w-4 h-4" />
                <span className="text-sm font-semibold">Prietenii mei</span>
              </button>
              <button
                onClick={() => navigate('/community/messages')}
                className="flex items-center gap-3 px-5 py-3 bg-white/15 hover:bg-white/25 rounded-xl text-white transition-all border border-white/20"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-semibold">Mesaje private</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SEARCH BAR (overlapping hero) ─── */}
      <div className="px-4 -mt-6 mb-6 max-w-5xl mx-auto">
        <div className="relative shadow-lg rounded-2xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Caută o comunitate sau categorie..."
            className="w-full pl-12 pr-12 py-4 border-0 rounded-2xl focus:ring-2 focus:ring-blue-500 bg-white text-sm shadow-sm"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      <div className="px-4 max-w-5xl mx-auto">

        {/* ─── MOBILE: Quick Actions ─── */}
        <div className="md:hidden grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => navigate('/community/friends')}
            className="group flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md active:scale-95 transition-all text-left"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white flex-shrink-0 shadow-sm">
              <UserPlus className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm">Prietenii mei</p>
              <p className="text-xs text-gray-500 mt-0.5">Conexiunile tale</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 flex-shrink-0 transition-colors" />
          </button>

          <button
            onClick={() => navigate('/community/messages')}
            className="group flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md active:scale-95 transition-all text-left"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white flex-shrink-0 shadow-sm">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm">Mesaje</p>
              <p className="text-xs text-gray-500 mt-0.5">Conversații private</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-emerald-500 flex-shrink-0 transition-colors" />
          </button>
        </div>

        {/* ─── DESKTOP LAYOUT: Sidebar + Main ─── */}
        <div className="flex flex-col md:flex-row gap-6">

          {/* ─── DESKTOP SIDEBAR ─── */}
          <aside className="hidden md:block w-64 flex-shrink-0 space-y-4">
            {/* Category filter list */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Categorii</h3>
              <div className="space-y-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                    filter === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>Toate</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${filter === 'all' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {diseases.length}
                  </span>
                </button>
                {categories.map(category => {
                  const meta = CATEGORY_META[category] || DEFAULT_META;
                  const Icon = meta.icon;
                  const count = diseases.filter(d => d.category === category).length;
                  return (
                    <button
                      key={category}
                      onClick={() => setFilter(filter === category ? 'all' : category)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                        filter === category
                          ? `bg-gradient-to-r ${meta.gradient} text-white shadow-sm`
                          : `text-gray-700 hover:bg-gray-50`
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1 text-left truncate">{CATEGORY_TRANSLATIONS[category] || category}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${filter === category ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Top communities sidebar widget */}
            {topCommunities.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-orange-500" />
                  Cele mai active
                </h3>
                <div className="space-y-2">
                  {topCommunities.map((disease, idx) => {
                    const meta = CATEGORY_META[disease.category] || DEFAULT_META;
                    const Icon = meta.icon;
                    return (
                      <button
                        key={disease.code}
                        onClick={() => navigate(`/community/${disease.code}`)}
                        className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-all text-left group"
                      >
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${meta.gradient} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate">{disease.name}</p>
                          <p className="text-xs text-gray-400">{disease.memberCount} membri</p>
                        </div>
                        <span className="text-xs font-bold text-gray-300">#{idx + 1}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </aside>

          {/* ─── MAIN CONTENT ─── */}
          <div className="flex-1 min-w-0">

            {/* MOBILE: Top communities horizontal scroll */}
            {!search && filter === 'all' && topCommunities.length > 0 && (
              <div className="md:hidden mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-orange-500" />
                    Cele mai active
                  </h2>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                  {topCommunities.map(disease => {
                    const meta = CATEGORY_META[disease.category] || DEFAULT_META;
                    const Icon = meta.icon;
                    return (
                      <button
                        key={disease.code}
                        onClick={() => navigate(`/community/${disease.code}`)}
                        className="flex-shrink-0 w-36 bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:shadow-md active:scale-95 transition-all text-left"
                      >
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center mb-3 shadow-sm`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-xs font-bold text-gray-900 leading-tight line-clamp-2 mb-2">{disease.name}</p>
                        <div className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                          <span className="text-xs text-gray-500">{disease.memberCount} membri</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* MOBILE: Category filter pills */}
            <div className="md:hidden mb-4">
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                <button
                  onClick={() => setFilter('all')}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                    filter === 'all' ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200'
                  }`}
                >
                  Toate ({diseases.length})
                </button>
                {categories.map(category => {
                  const meta = CATEGORY_META[category] || DEFAULT_META;
                  const Icon = meta.icon;
                  const count = diseases.filter(d => d.category === category).length;
                  return (
                    <button
                      key={category}
                      onClick={() => setFilter(filter === category ? 'all' : category)}
                      className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                        filter === category
                          ? `bg-gradient-to-r ${meta.gradient} text-white shadow-sm`
                          : `bg-white ${meta.text} border ${meta.border}`
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {CATEGORY_TRANSLATIONS[category] || category} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Desktop section header */}
            <div className="hidden md:flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                {filter === 'all'
                  ? 'Toate comunitățile'
                  : CATEGORY_TRANSLATIONS[filter] || filter}
                <span className="ml-2 text-sm font-normal text-gray-400">
                  ({filteredDiseases.length} {filteredDiseases.length === 1 ? 'comunitate' : 'comunități'})
                </span>
              </h2>
              {(search || filter !== 'all') && (
                <button
                  onClick={() => { setSearch(''); setFilter('all'); }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Resetează filtrele
                </button>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {/* Mobile results count */}
            {(search || filter !== 'all') && (
              <p className="md:hidden text-xs text-gray-500 mb-3">
                {filteredDiseases.length} {filteredDiseases.length === 1 ? 'rezultat' : 'rezultate'}
                {search && <span> pentru „<strong>{search}</strong>"</span>}
              </p>
            )}

            {/* ─── COMMUNITIES GRID ─── */}
            {/* Mobile: single column | Desktop: 2 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {filteredDiseases.map(disease => (
                <DiseaseCard
                  key={disease.code}
                  disease={disease}
                  searchQuery={search}
                  onClick={() => navigate(`/community/${disease.code}`)}
                />
              ))}
            </div>

            {filteredDiseases.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-7 h-7 text-gray-400" />
                </div>
                <p className="text-gray-600 font-semibold mb-1">Nu s-au găsit comunități</p>
                <p className="text-gray-400 text-sm mb-4">Încearcă alt termen de căutare</p>
                {search && (
                  <button
                    onClick={() => { setSearch(''); setFilter('all'); }}
                    className="text-blue-600 text-sm font-medium hover:underline"
                  >
                    Șterge filtrele
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface DiseaseCardProps {
  disease: Disease;
  onClick: () => void;
  searchQuery?: string;
}

function highlightText(text: string, query: string) {
  if (!query.trim()) return <>{text}</>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-200 text-gray-900 rounded px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function DiseaseCard({ disease, onClick, searchQuery = '' }: DiseaseCardProps) {
  const meta = CATEGORY_META[disease.category] || DEFAULT_META;
  const Icon = meta.icon;
  const isActive = disease.postCount > 0;

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:shadow-md active:scale-[0.99] transition-all group"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform`}>
          <Icon className="w-5 h-5 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <span className={`inline-block px-2 py-0.5 ${meta.bg} ${meta.text} text-xs font-medium rounded-full mb-1`}>
                {CATEGORY_TRANSLATIONS[disease.category] || disease.category}
              </span>
              <h3 className="text-sm font-bold text-gray-900 leading-tight">
                {highlightText(disease.name, searchQuery)}
              </h3>
              {disease.shortDescription && (
                <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">
                  {disease.shortDescription}
                </p>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 flex-shrink-0 mt-1 transition-colors" />
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-gray-50">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Users className="w-3.5 h-3.5" />
              <span className="font-semibold text-gray-700">{disease.memberCount}</span>
              <span>membri</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-gray-300"></div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <MessageCircle className="w-3.5 h-3.5" />
              <span className="font-semibold text-gray-700">{disease.postCount}</span>
              <span>postări</span>
            </div>
            {isActive && (
              <>
                <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                <div className="flex items-center gap-1 text-xs text-green-600 font-medium">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                  Activă
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
